import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Layers, Loader2, Plus } from "lucide-react";
import { api } from "@/api/client";
import { downloadBlob } from "@/lib/download";
import { usePromptsStore } from "@/stores/prompts";
import { Button } from "@/components/ui/button";
import { AddPromptForm } from "./AddPromptForm";
import { PromptsTable } from "./PromptsTable";
import { EditPromptModal } from "./EditPromptModal";
import type { Prompt } from "../../../types";

const promptsCsvFilename = "prompts.csv";

/**
 * Gets a safe display message for unknown caught errors.
 *
 * @param error Caught error value.
 * @param fallback Fallback message.
 * @return Displayable error message.
 */
function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;

  return fallback;
}

/**
 * Renders the prompt management screen.
 *
 * @return Prompt management view.
 */
export function PromptsView() {
  const fetch = usePromptsStore((s) => s.fetch);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [promptNotice, setPromptNotice] = useState("");

  useEffect(() => {
    fetch();
  }, []);

  /**
   * Exports prompts as a workspace-scoped CSV download.
   *
   * @return Promise that resolves after the download is triggered.
   */
  const handleExportCsv = async (): Promise<void> => {
    setIsExporting(true);
    setExportError("");

    try {
      const csvBlob = await api.prompts.exportCsv();
      downloadBlob(csvBlob, promptsCsvFilename);
    } catch (error) {
      setExportError(getErrorMessage(error, "Failed to export prompts"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Legacy-surface pointer: prompts are now owned by topic spaces. */}
      <div className="flex items-center gap-2 rounded-lg border border-brand-100 bg-brand-50/50 px-4 py-3 text-[13px] text-slate-700">
        <Layers className="w-4 h-4 text-brand-500 shrink-0" />
        <span>
          Topics now own prompts, competitors, and tracking — manage them from a topic space.{" "}
          <Link to="/app/health" className="font-medium text-brand-600 hover:underline">Go to Brand Health →</Link>
        </span>
      </div>

      {/* Actions Bar */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleExportCsv}
            disabled={isExporting}
            aria-busy={isExporting}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Button type="button" onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Create Prompt
          </Button>
        </div>
        {exportError && (
          <p className="text-right text-[13px] text-red-600" role="alert">
            {exportError}
          </p>
        )}
      </div>

      {promptNotice && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3" role="status">
          <p className="text-[13px] text-green-700">{promptNotice}</p>
        </div>
      )}

      {/* Collapsible Add Form */}
      {showForm && (
        <div className="animate-fade-up">
          <AddPromptForm onSuccess={() => setShowForm(false)} />
        </div>
      )}

      {/* Table */}
      <PromptsTable
        onEdit={setEditingPrompt}
        onDeleteComplete={(mode) => {
          setPromptNotice(
            mode === "archived"
              ? "Prompt archived and hidden from new runs. Historical results still reference it."
              : "Prompt deleted.",
          );
        }}
      />

      {/* Edit Modal */}
      <EditPromptModal
        prompt={editingPrompt}
        open={!!editingPrompt}
        onOpenChange={(open) => { if (!open) setEditingPrompt(null); }}
      />
    </div>
  );
}
